import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from "axios";
import {
    TwoFactorAuthState,
    TwoFactorAuthValidateCodeResponse,
    GenerateQrCodeUrlResponse,
    TwoFactorAuthValidateCodeRequest
} from './_authTypes';
import { handleError } from '../../utils/handleError';
import { logout } from './authSlice';
import { handleAxiosError } from '../../axios/handleAxiosError';
import { buildApiUrl } from '../../axios/urlCreator';

const initialState: TwoFactorAuthState = {
    twoFAOtpCodeResponse: null,
    generateQrCodeUrlResponse: null,
    loading: false,
    error: null
};


const twoFactorAuthSlice = createSlice({
    name: 'twoFactorAuth',
    initialState,
    reducers: {
        set2FAOtpCodeResponse(state, action: PayloadAction<TwoFactorAuthValidateCodeResponse | null>) {
            const validateOtpCode = action.payload;
            state.twoFAOtpCodeResponse = validateOtpCode;
        },
        clear2FAQrCodeUrl(state) {
            state.generateQrCodeUrlResponse = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(twoFactorAuthValidateOtpCode.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(twoFactorAuthValidateOtpCode.fulfilled, (state, action) => {
                state.loading = false;
                state.twoFAOtpCodeResponse = action.payload;
                state.error = null;
            })
            .addCase(twoFactorAuthValidateOtpCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(get2FAQrCodeUrl.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(get2FAQrCodeUrl.fulfilled, (state, action) => {
                state.loading = false;
                state.generateQrCodeUrlResponse = action.payload;
                state.error = null;
            })
            .addCase(get2FAQrCodeUrl.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(logout, (state) => {
                state.twoFAOtpCodeResponse = null;
            });
    }
});

export const twoFactorAuthValidateOtpCode = createAsyncThunk<TwoFactorAuthValidateCodeResponse, TwoFactorAuthValidateCodeRequest>(
    'auth/twoFactorAuthValidateOtpCode',
    async (twoFactorAuthValidateCodeRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<TwoFactorAuthValidateCodeResponse>(buildApiUrl("twoFactorAuthentication/validateOTP"), twoFactorAuthValidateCodeRequest);

            if (response.data.validation) {
                sessionStorage.removeItem("oodd");
                sessionStorage.removeItem("pppfff");
            }

            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

export const get2FAQrCodeUrl = createAsyncThunk<GenerateQrCodeUrlResponse, void>(
    'auth/get2FAQrCodeUrl',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<GenerateQrCodeUrlResponse>(buildApiUrl("twoFactorAuthentication/generateQrCodeUrl"));
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(handleAxiosError(error));
            }
            return rejectWithValue(handleError(error));
        }
    }
);

export const { set2FAOtpCodeResponse, clear2FAQrCodeUrl } = twoFactorAuthSlice.actions;
export default twoFactorAuthSlice.reducer;
